import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Ayuda } from './Ayuda';
import type { ClaveGlosario } from '@/lib/glosario';

/** Primitivas de tabla densa, compartidas por todas las pantallas. */
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('w-full overflow-x-auto', className)}>
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

/** `wrap` deja que el título ocupe dos líneas. Sirve para etiquetas largas ("Tiempo de
 *  Producción") en tablas anchas: sin esto el encabezado estira la columna y empuja al resto
 *  fuera de pantalla, aunque los valores de abajo sean cortos. */
export function Thead({
  columns,
}: {
  columns: {
    key: string;
    label: string;
    align?: 'left' | 'right' | 'center';
    wrap?: boolean;
    className?: string;
    /** Término del glosario: pinta el signo de interrogación con su explicación. */
    ayuda?: ClaveGlosario;
  }[];
}) {
  return (
    <thead>
      <tr className="border-b border-hairline">
        {columns.map((col) => (
          <th
            key={col.key}
            className={cn(
              'px-4 py-2.5 text-2xs font-semibold uppercase tracking-wider text-ink-faint',
              col.wrap ? 'max-w-[7rem]' : 'whitespace-nowrap',
              col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left',
              col.className,
            )}
          >
            {/* La ayuda va en el ENCABEZADO y no en cada celda: el término es de la columna,
                y repetir el ícono en 700 filas sería ruido en vez de auxilio. */}
            {col.ayuda ? (
              // El ícono va SIEMPRE después del rótulo. Invertirlo en las columnas alineadas
              // a la derecha (que fue el primer intento) lo pega contra la columna anterior y
              // parece pertenecer a esa: se lee "ⓘ DEMANDA/DÍA" como si el ícono fuera de
              // Stock. Se vio en el encabezado, no en el código.
              <Ayuda termino={col.ayuda} className="font-semibold uppercase tracking-wider">
                <span>{col.label}</span>
              </Ayuda>
            ) : (
              col.label
            )}
          </th>
        ))}
      </tr>
    </thead>
  );
}

export function Tr({ children, className }: { children: ReactNode; className?: string }) {
  return <tr className={cn('border-b border-hairline/60 transition-colors hover:bg-subtle', className)}>{children}</tr>;
}

export function Td({
  children,
  className,
  align = 'left',
}: {
  children: ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      className={cn(
        'px-4 py-3 align-middle text-ink',
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
        className,
      )}
    >
      {children}
    </td>
  );
}
